import { A11yModule } from '@angular/cdk/a11y';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { DateAdapter } from '../adapters/abstract-date-adapter';
import { CalendarView } from '../date-picker';
import { HxMonthView } from './month-view';
import { HxMultiYearView } from './multi-year-view';
import { HxYearView } from './year-view';

/**
 * Calendar component for date selection. Contains header, view components, and navigation logic.
 *
 * @example
 *   ```html
 *   <hx-calendar
 *     [activeDate]="activeDate"
 *     [selected]="selectedDate"
 *     [minDate]="minDate"
 *     [maxDate]="maxDate"
 *     [startView]="'month'"
 *     (selectedChange)="onDateSelected($event)"
 *     (viewChange)="onViewChange($event)"
 *   />
 *   ```;
 */
@Component({
  selector: 'hx-calendar',
  standalone: true,
  imports: [A11yModule, HxMonthView, HxYearView, HxMultiYearView],
  templateUrl: './calendar.html',
  styleUrls: ['./calendar.css', './calendar-tokens.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'hx-calendar',
    '[class.hx-calendar--day-view]': "currentView() === 'month'",
    '[class.hx-calendar--month-view]': "currentView() === 'year'",
    '[class.hx-calendar--year-view]': "currentView() === 'multi-year'",
    role: 'dialog',
    '[attr.aria-label]': '"Choose a date"',
    '[attr.aria-modal]': 'true',
    cdkTrapFocus: '',
    '[cdkTrapFocusAutoCapture]': 'true',
  },
})
export class HxCalendar<D = Date> {
  private readonly elementRef = inject(ElementRef);

  /** Focus the currently active cell in the calendar. */
  focusActiveCell(): void {
    const el = this.elementRef.nativeElement as HTMLElement;
    const activeCell = el.querySelector<HTMLElement>('.hx-calendar-body__cell[tabindex="0"]');
    activeCell?.focus();
  }
  // =========================================================================
  // Inputs
  // =========================================================================

  /** Currently active (focused) date in the calendar. */
  readonly activeDate = input.required<D>();

  /** Currently selected date or range. */
  readonly selected = input<D | null>(null);

  /** Range start date (for range selection mode). */
  readonly rangeStart = input<D | null>(null);

  /** Range end date (for range selection mode). */
  readonly rangeEnd = input<D | null>(null);

  /** Minimum selectable date. */
  readonly minDate = input<D | null>(null);

  /** Maximum selectable date. */
  readonly maxDate = input<D | null>(null);

  /** Custom date filter function. */
  readonly dateFilter = input<((date: D) => boolean) | null>(null);

  /** Allowed years for date selection. If set, only dates in these years are selectable. */
  readonly allowedYears = input<number[] | null>(null);

  /** Initial view to display. */
  readonly startView = input<CalendarView>('month');

  /** When true, selecting a month in year view finalizes selection instead of drilling down to day selection. */
  readonly finalizeOnMonthSelection = input<boolean>(false);

  /** Date adapter for date operations. */
  readonly dateAdapter = input.required<DateAdapter<D>>();

  // =========================================================================
  // Outputs
  // =========================================================================

  /** Emits when a date is selected. */
  readonly selectedChange = output<D>();

  /** Emits when the active date changes (navigation). */
  readonly activeDateChange = output<D>();

  /** Emits when the view changes. */
  readonly viewChange = output<CalendarView>();

  /** Emits when a month is selected in year view. */
  readonly monthSelected = output<D>();

  /** Emits when a year is selected in multi-year view. */
  readonly yearSelected = output<D>();

  // =========================================================================
  // Constructor
  // =========================================================================

  constructor() {
    // Sync currentView with startView input when it changes
    effect(() => {
      const view = this.startView();
      this.currentView.set(view);
    });
  }

  // =========================================================================
  // State
  // =========================================================================

  /** Current calendar view. */
  protected readonly currentView = signal<CalendarView>('month');

  /** Period label for header (e.g., "January 2025", "2025", "2020 - 2043"). */
  protected readonly periodLabel = computed(() => {
    const adapter = this.dateAdapter();
    const active = this.activeDate();
    const view = this.currentView();

    if (view === 'month') {
      const monthNames = adapter.getMonthNames('long');
      const month = adapter.getMonth(active);
      const year = adapter.getYear(active);
      return `${monthNames[month]} ${year}`;
    } else if (view === 'year') {
      return `${adapter.getYear(active)}`;
    } else {
      // Multi-year view shows range
      const year = adapter.getYear(active);
      const startYear = Math.floor(year / 12) * 12;
      const endYear = startYear + 11;
      return `${startYear} \u2013 ${endYear}`;
    }
  });

  // =========================================================================
  // Navigation
  // =========================================================================

  /** True when the previous period lies entirely before minDate. */
  protected readonly previousDisabled = computed(() => this.isNavigationBlocked(-1));

  /** True when the next period lies entirely after maxDate. */
  protected readonly nextDisabled = computed(() => this.isNavigationBlocked(1));

  /** Navigate to previous period. */
  protected goPrevious(): void {
    if (this.previousDisabled()) {
      return;
    }

    this.activeDateChange.emit(this.shiftActiveDate(-1));
  }

  /** Navigate to next period. */
  protected goNext(): void {
    if (this.nextDisabled()) {
      return;
    }

    this.activeDateChange.emit(this.shiftActiveDate(1));
  }

  /** Active date moved one period in the given direction, sized to the current view. */
  private shiftActiveDate(direction: -1 | 1): D {
    const adapter = this.dateAdapter();
    const active = this.activeDate();
    const view = this.currentView();

    if (view === 'month') {
      return adapter.addCalendarMonths(active, direction);
    }

    return adapter.addCalendarYears(active, direction * (view === 'year' ? 1 : 12));
  }

  /** MinDate/maxDate only disable individual cells, so navigation past them is blocked here. */
  private isNavigationBlocked(direction: -1 | 1): boolean {
    const bound = direction < 0 ? this.minDate() : this.maxDate();
    if (!bound) {
      return false;
    }

    const adapter = this.dateAdapter();
    const target = this.shiftActiveDate(direction);
    const [start, end] = this.periodBounds(target);

    return direction < 0 ? adapter.compareDate(end, bound) < 0 : adapter.compareDate(start, bound) > 0;
  }

  /** First and last day covered by the period containing the given date, for the current view. */
  private periodBounds(date: D): [D, D] {
    const adapter = this.dateAdapter();
    const view = this.currentView();

    if (view === 'month') {
      const start = this.startOfMonth(date);
      return [start, adapter.addCalendarDays(start, adapter.getNumDaysInMonth(date) - 1)];
    }

    const yearsInBlock = view === 'year' ? 1 : 12;
    const blockStartYear = view === 'year' ? adapter.getYear(date) : Math.floor(adapter.getYear(date) / 12) * 12;
    const firstYear = adapter.addCalendarYears(date, blockStartYear - adapter.getYear(date));
    const start = this.startOfMonth(adapter.addCalendarMonths(firstYear, -adapter.getMonth(firstYear)));
    const lastMonth = adapter.addCalendarMonths(start, yearsInBlock * 12 - 1);

    return [start, adapter.addCalendarDays(lastMonth, adapter.getNumDaysInMonth(lastMonth) - 1)];
  }

  private startOfMonth(date: D): D {
    const adapter = this.dateAdapter();
    return adapter.addCalendarDays(date, 1 - adapter.getDate(date));
  }

  /** Switch to broader view when clicking period label. */
  protected switchToBroaderView(): void {
    const view = this.currentView();

    if (view === 'month') {
      this.currentView.set('year');
      this.viewChange.emit('year');
    } else if (view === 'year') {
      this.currentView.set('multi-year');
      this.viewChange.emit('multi-year');
    }
    setTimeout(() => {
      this.focusActiveCell();
    });
  }

  /** Handle active date changes from month/year/multi-year views. */
  protected onActiveDateChange(date: D): void {
    this.activeDateChange.emit(date);
  }

  /** Handle date selection from view components. */
  protected onDateSelected(date: D): void {
    const view = this.currentView();

    if (view === 'month') {
      // Final selection
      this.selectedChange.emit(date);
    } else if (view === 'year') {
      // Month selected
      this.monthSelected.emit(date);

      // If finalizeOnMonthSelection is true, emit selection instead of drilling down
      if (this.finalizeOnMonthSelection()) {
        this.selectedChange.emit(date);
      } else {
        // Otherwise, switch to month view for day selection
        this.activeDateChange.emit(date);
        this.currentView.set('month');
        this.viewChange.emit('month');
        setTimeout(() => {
          this.focusActiveCell();
        });
      }
    } else {
      // Year selected in multi-year, switch to year view
      this.yearSelected.emit(date);
      this.activeDateChange.emit(date);
      this.currentView.set('year');
      this.viewChange.emit('year');
      setTimeout(() => {
        this.focusActiveCell();
      });
    }
  }
}
